import { useMemo } from 'react';
import useCalendar from './calendar/useCalendar';
import useCards, { getAllCardMediaWithTypes } from './cards/useCards';
import useCollections, {
	fullCollectionLength,
} from './collections/useCollections';
import useSpecies from './species/useSpecies';

export const AchievementNotificationPriority = {
	FIRST: 2,
	MIDDLE: 1,
	LAST: 0,
	HIDE_WHEN_TOO_MANY: -1,
	HIDDEN: -2,
} as const;

export interface AchievementCalculation {
	achievedTimes: number;
	totalScore: number;
	explanation: string;
	scorePerSpecies: {
		speciesId: number;
		score: number;
	}[];
}

export interface Achievement {
	id: string;
	title: string;
	description: string;
	notificationPriority: (typeof AchievementNotificationPriority)[keyof typeof AchievementNotificationPriority];
	isVisibleWhenNotAchieved: boolean;
	calculate: (using: {
		species: ReturnType<typeof useSpecies>;
		calendar: ReturnType<typeof useCalendar>;
		collections: ReturnType<typeof useCollections>;
		cards: ReturnType<typeof useCards>;
	}) => AchievementCalculation;
}

const noScore: ReturnType<Achievement['calculate']> = {
	achievedTimes: 0,
	totalScore: 0,
	scorePerSpecies: [],
	explanation: '',
};

const achievementTypes: Achievement[] = [
	{
		id: 'first-photo',
		title: 'Eerste foto',
		description: 'Je eerste foto is herkend.',
		notificationPriority: AchievementNotificationPriority.MIDDLE,
		isVisibleWhenNotAchieved: true,
		calculate: ({ cards: useCards }) => {
			const baseScore = 5;
			const { cards } = useCards;

			const automaticallyIdentifiedPhoto = cards.find((card) =>
				card.photos.some(
					(photo) => photo.identifiedBy === 'automatically',
				),
			);

			if (!automaticallyIdentifiedPhoto) return noScore;

			// Give base score only once
			return {
				achievedTimes: 1,
				totalScore: baseScore,
				explanation: 'Jippie!',
				scorePerSpecies: automaticallyIdentifiedPhoto
					? [
						{
							speciesId:
								automaticallyIdentifiedPhoto.speciesId,
							score: baseScore,
						},
					]
					: [],
			};
		},
	},
	{
		id: 'first-audio',
		title: 'Eerste geluid',
		description: 'Je eerste geluid is herkend.',
		notificationPriority: AchievementNotificationPriority.MIDDLE,
		isVisibleWhenNotAchieved: true,
		calculate: ({ cards: useCards }) => {
			const baseScore = 100;
			const { cards } = useCards;

			const automaticallyIdentifiedAudio = cards.find((card) =>
				(card.audio ?? []).some(
					(photo) => photo.identifiedBy === 'automatically',
				),
			);

			if (!automaticallyIdentifiedAudio) return noScore;

			// Give base score only once
			return {
				achievedTimes: 1,
				totalScore: baseScore,
				explanation: 'Jippie!',
				scorePerSpecies: automaticallyIdentifiedAudio
					? [
						{
							speciesId:
								automaticallyIdentifiedAudio.speciesId,
							score: baseScore,
						},
					]
					: [],
			};
		},
	},
	{
		id: 'conductor',
		title: 'Dirigent',
		description: 'Het lijkt wel een orkest!',
		notificationPriority: AchievementNotificationPriority.MIDDLE,
		isVisibleWhenNotAchieved: true,
		calculate: ({ cards: useCards }) => {
			const baseScore = 35;
			const { cards } = useCards;

			const automaticallyIdentifiedAudio = cards.filter((card) =>
				(card.audio ?? []).some(
					(photo) => photo.identifiedBy === 'automatically',
				),
			);

			// Show the achievement only when there are at least 2 automatically identified audio items
			if (automaticallyIdentifiedAudio.length < 2) return noScore;

			// Give base score for every audio item
			return {
				achievedTimes: automaticallyIdentifiedAudio.length,
				totalScore: baseScore * automaticallyIdentifiedAudio.length,
				explanation: `Wel ${automaticallyIdentifiedAudio.length} geluiden zitten in jouw verzameling.`,
				scorePerSpecies: automaticallyIdentifiedAudio
					? automaticallyIdentifiedAudio.map((card) => ({
						speciesId: card.speciesId,
						score: baseScore,
					}))
					: [],
			};
		},
	},
	{
		id: 'collection-complete',
		title: 'Beestachtige baas',
		description: 'Je hebt de hele verzameling compleet!',
		notificationPriority: AchievementNotificationPriority.FIRST,
		isVisibleWhenNotAchieved: true,
		calculate: ({ species: useSpecies, cards: useCards }) => {
			const { species } = useSpecies;
			const { cards } = useCards;

			const collectedSpecies = new Set(
				cards.map((card) => card.speciesId)
			);

			// If all species have been collected, the achievement is unlocked
			if (species && species.data.length > 0 && collectedSpecies.size === species.data.length) {
				const totalScore = species.data.length * 100;

				return {
					achievedTimes: 1,
					totalScore,
					explanation: `Dat zijn wel ${species.data.length} soorten!`,
					scorePerSpecies: Array.from(collectedSpecies).map((speciesId) => ({
						speciesId,
						score: totalScore / collectedSpecies.size,
					})),
				};
			}

			return noScore;
		},
	},
	{
		id: 'species-collected',
		title: 'Soort gescoord!',
		description: 'Een nieuwe soort voor je verzameling.',
		notificationPriority: AchievementNotificationPriority.FIRST,
		isVisibleWhenNotAchieved: true,
		calculate: ({ cards: useCards }) => {
			const baseScore = 10;
			const { cards } = useCards;

			const automaticallyIdentifiedMedia = cards.filter((card) =>
				getAllCardMediaWithTypes(card).some(
					(media) => media.identifiedBy === 'automatically',
				),
			);

			// Give base score for every card
			return {
				achievedTimes: automaticallyIdentifiedMedia.length,
				totalScore: automaticallyIdentifiedMedia.length * baseScore,
				explanation: `Je hebt nu ${automaticallyIdentifiedMedia.length} keer een soort gescoord!`,
				scorePerSpecies: automaticallyIdentifiedMedia.map((card) => ({
					speciesId: card.speciesId,
					score: baseScore,
				})),
			};
		},
	},
	{
		id: 'calendar-streak',
		title: 'Langste reeks',
		description: 'Een rijtje soorten op de kalender.',
		notificationPriority: AchievementNotificationPriority.MIDDLE,
		isVisibleWhenNotAchieved: true,
		calculate: ({ cards: useCards, calendar: useCalendar }) => {
			const baseScore = 40;
			const { cards } = useCards;
			const { calendar } = useCalendar;

			if (!calendar) return noScore;

			// Get a boolean array of whether a card has been collected on a date
			const calendarCollected: boolean[] = calendar.data.map((entry) =>
				cards.some(
					(card) =>
						// Check if the card is of the species and if it has been identified by the AI
						card.speciesId ===
						entry.attributes.speciesEntry.data?.id &&
						getAllCardMediaWithTypes(card).some(
							(media) => media.identifiedBy === 'automatically',
						),
				),
			);

			// Calculate the longest streak (contiguous trues), and remember its start index
			let longestStreak = 0;
			let currentStreak = 0;
			let currentStartIndex = 0;
			let bestStartIndex = -1;
			for (let i = 0; i < calendarCollected.length; i++) {
				const collected = calendarCollected[i];
				if (collected) {
					if (currentStreak === 0) currentStartIndex = i;
					currentStreak++;
				} else {
					if (currentStreak > longestStreak) {
						longestStreak = currentStreak;
						bestStartIndex = currentStartIndex;
					}
					currentStreak = 0;
				}
			}
			if (currentStreak > longestStreak) {
				longestStreak = currentStreak;
				bestStartIndex = currentStartIndex;
			}

			// If the longest streak is 1, the user has not achieved a streak
			const hasAchievedAStreak = longestStreak > 1;
			if (!hasAchievedAStreak) return noScore;

			// Collect species IDs for the longest streak segment
			const longestStreakSpeciesIds: number[] = [];
			for (let i = bestStartIndex; i < bestStartIndex + longestStreak; i++) {
				const id = calendar.data[i]?.attributes.speciesEntry.data?.id;
				if (typeof id === 'number') longestStreakSpeciesIds.push(id);
			}
			return {
				achievedTimes: longestStreak,
				totalScore: longestStreak * baseScore,
				explanation: `Je langste reeks is ${longestStreak} soorten achter elkaar!`,
				scorePerSpecies: longestStreakSpeciesIds.map((speciesId) => ({
					speciesId,
					score: baseScore,
				})),
			};
		},
	},
	{
		id: 'species-variants',
		title: 'Soortensoort',
		description: 'Er zijn verschillende namen van dezelfde soort.',
		notificationPriority: AchievementNotificationPriority.MIDDLE,
		isVisibleWhenNotAchieved: true,
		calculate: ({ cards: useCards }) => {
			const baseScore = 25;
			const { cards } = useCards;

			let achievedTimes = 0;
			// For each species (card), count extra variants (>= 2 unique identifiedAs) and add one entry per extra variant
			const scorePerSpecies: { speciesId: number; score: number }[] = [];
			for (const card of cards) {
				const variantsCount = new Set(
					getAllCardMediaWithTypes(card)
						.filter((media) => media.identifiedBy === 'automatically')
						.map((media) => media.identifiedAs),
				).size;
				const extraVariants = Math.max(0, variantsCount - 1);
				if (extraVariants > 0) {
					achievedTimes += extraVariants;
					for (let i = 0; i < extraVariants; i++) {
						scorePerSpecies.push({ speciesId: card.speciesId, score: baseScore });
					}
				}
			}

			return {
				achievedTimes,
				totalScore: baseScore * achievedTimes,
				explanation: `Je hebt ${achievedTimes} extra ${achievedTimes === 1 ? 'soort' : 'soorten'
					} gescoord!`,
				scorePerSpecies,
			};
		},
	},
	{
		id: 'species-of-the-week',
		title: 'Soort van de week',
		description: 'De juiste soort in de juiste week.',
		notificationPriority: AchievementNotificationPriority.MIDDLE,
		isVisibleWhenNotAchieved: true,
		calculate: ({ cards: useCards, calendar: useCalendar }) => {
			const baseScore = 20;
			const { cards } = useCards;
			const { getDatesFromSpecies } = useCalendar;

			// For every card, check if there is a media item with a timestamp within 7 days of the species of the species date
			const collectedSpeciesOfTheWeek = cards.filter((card) => {
				const speciesDates = getDatesFromSpecies(card.speciesId);
				const cardDates = getAllCardMediaWithTypes(card).map(
					(media) => new Date(media.timestamp),
				);

				return speciesDates.some((speciesDate) =>
					cardDates.some(
						(cardDate) =>
							Math.abs(
								Math.floor(
									(cardDate.getTime() -
										speciesDate.getTime()) /
									(1000 * 60 * 60 * 24),
								),
							) <= 7,
					),
				);
			});

			const score = collectedSpeciesOfTheWeek.length * baseScore;

			return {
				achievedTimes: collectedSpeciesOfTheWeek.length,
				totalScore: score,
				explanation: `Je hebt ${collectedSpeciesOfTheWeek.length} keer de soort van de week gescoord!`,
				scorePerSpecies: collectedSpeciesOfTheWeek.map((card) => ({
					speciesId: card.speciesId,
					score: baseScore,
				})),
			};
		},
	},
	{
		id: 'full-collections',
		title: 'Retteketet een kwartet!',
		description: 'Je kwartet is vol.',
		notificationPriority: AchievementNotificationPriority.MIDDLE,
		isVisibleWhenNotAchieved: true,
		calculate: ({ cards: useCards, collections: useCollections }) => {
			const baseScore = 100;

			const { cards } = useCards;
			const { collections } = useCollections;

			if (!collections) return noScore;

			let score = 0;

			for (const collection of collections.data) {
				const speciesEntries =
					collection.attributes?.speciesEntries?.data;
				if (!speciesEntries) continue;

				const collectedSpecies = speciesEntries.filter((entry) =>
					cards.some(
						(card) =>
							// Check if the card is of the species and if it has been identified by the AI
							card.speciesId === entry.id &&
							getAllCardMediaWithTypes(card).some(
								(media) =>
									media.identifiedBy === 'automatically',
							),
					),
				);

				if (collectedSpecies.length >= fullCollectionLength) {
					score += baseScore;
				}
			}

			return {
				achievedTimes: score / baseScore,
				totalScore: score,
				explanation: `Je hebt ${score / baseScore} ${score / baseScore === 1 ? 'kwartet' : 'kwartetten'
					}!`,
				scorePerSpecies: [],
			};
		},
	},
	{
		id: 'expert-species',
		title: 'Een echte expert',
		description: 'Knap! Dit is een moeilijke.',
		notificationPriority: AchievementNotificationPriority.MIDDLE,
		isVisibleWhenNotAchieved: true,
		calculate: ({ cards: useCards, species: useSpecies }) => {
			const baseScore = 150;

			const { cards } = useCards;
			const { findSpeciesById } = useSpecies;

			// Count how many species have been collected that have level === 'expert'
			const collectedExpertSpecies = cards.filter(
				(card) =>
					findSpeciesById(card.speciesId)?.attributes.level ===
					'expert' &&
					// Also check if the species has been identified by the AI
					getAllCardMediaWithTypes(card).some(
						(media) => media.identifiedBy === 'automatically',
					),
			);
			const score = collectedExpertSpecies.length * baseScore;

			return {
				achievedTimes: score / baseScore,
				totalScore: score,
				explanation: `Je hebt er hier al ${collectedExpertSpecies.length} van.`,
				scorePerSpecies: collectedExpertSpecies.map((card) => ({
					speciesId: card.speciesId,
					score: baseScore,
				})),
			};
		},
	},
	{
		id: 'day',
		title: 'Dagen',
		description: 'Iedere dag dat je een soort spot, telt!',
		notificationPriority:
			AchievementNotificationPriority.HIDE_WHEN_TOO_MANY,
		isVisibleWhenNotAchieved: true,
		calculate: ({ cards: useCards }) => {
			const baseScore = 10;
			const { cards } = useCards;

			// Store dates per species
			const species: Record<number, string[]> = {};

			// Get all dates per species from the cards
			cards.forEach((card) => {
				const speciesId = card.speciesId;
				const cardDates = getAllCardMediaWithTypes(card).map(
					// Store only the date (year, month, day) of the media item
					(media) => new Date(media.timestamp).toDateString(),
				);

				if (!species[speciesId]) {
					species[speciesId] = [];
				}

				species[speciesId].push(...cardDates);
			});

			// Get a flat set of all dates
			const allDates = new Set(Object.values(species).flat());
			const numberOfDays = allDates.size;

			// Get the first species of a certain date
			const getFirstSpeciesOfDate = (date: string) => {
				return Object.entries(species).find(([, dates]) =>
					dates.includes(date),
				)?.[0];
			};

			// Only give the score if days are more than 2
			if (numberOfDays < 2) return noScore;

			return {
				achievedTimes: numberOfDays,
				totalScore: numberOfDays * baseScore,
				explanation: `Je bent al ${numberOfDays} dagen aan het spotten!`,
				scorePerSpecies: Array.from(allDates)
					.map((date) => {
						const speciesId = getFirstSpeciesOfDate(date);
						return {
							speciesId: Number(speciesId),
							score: baseScore,
						};
					})
					.filter((score) => score.speciesId),
			};
		},
	},
] as const;

export type CalculatedAchievement = Achievement & {
	calculatedAchievedTimes: number;
	calculatedTotalScore: number;
	calculatedExplanation: string;
	calculatedScorePerSpecies: AchievementCalculation['scorePerSpecies'];
};

const useAchievements = (speciesId?: number) => {
	const species = useSpecies();
	const calendar = useCalendar();
	const collections = useCollections();
	const cards = useCards();

	const achievements = useMemo<CalculatedAchievement[]>(() => {
		if (!species.species || !calendar.calendar || !collections.collections)
			return [];

		// Calculate for all achievements
		try {
			return (
				achievementTypes
					.map((achievement) => {
						const {
							achievedTimes,
							totalScore,
							explanation,
							scorePerSpecies,
						} = achievement.calculate({
							species,
							calendar,
							collections,
							cards,
						});

						return {
							...achievement,
							calculatedAchievedTimes: achievedTimes,
							calculatedTotalScore: totalScore,
							calculatedExplanation: explanation,
							calculatedScorePerSpecies: scorePerSpecies,
						};
					})
					// If a speciesId is provided, filter out achievements that are not related to that species
					.filter((achievement) => {
						if (!speciesId) return true;
						return achievement.calculatedScorePerSpecies.some(
							(scorePerSpecies) =>
								scorePerSpecies.speciesId === speciesId,
						);
					})
					// Sort them by score, highest score first
					.sort((a, b) => {
						return b.calculatedTotalScore - a.calculatedTotalScore;
					})
			);
		} catch (e) {
			console.error(e);
			return [];
		}
	}, [calendar, cards, collections, species, speciesId]);

	const score = useMemo(
		() =>
			achievements.reduce((acc, achievement) => {
				return acc + achievement.calculatedTotalScore;
			}, 0),
		[achievements],
	);

	return {
		achievements,
		score,
	};
};

export default useAchievements;
